import {IconBrain} from '@tabler/icons-react';
import type {FC} from 'react';
import type {AssistantMemoryResponse} from 'src/api/ProactApiClient';
import {AssistantMemorySourceEnum} from 'src/api/ProactApiClient';
import {Box} from 'src/design/atoms/Box';
import {Button} from 'src/design/atoms/Button';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {
  useAcceptAssistantMemoryMutation,
  useDeleteAssistantMemoryMutation,
  useGetAssistantMemoryQuery,
} from 'src/redux/queries/assistant';
import type {AssistantMemoryAction} from 'src/shared/assistant/streamAssistantMessage';
import {isProposedMemory, memoryKindLabel} from './assistantMemoryFormat';
import {useMemoryRequest} from './useMemoryRequest';

export interface AssistantMemoryCardProps {
  note: AssistantMemoryResponse;
  action?: AssistantMemoryAction;
  onManage: () => void;
}

/**
 * Memory note a Scout reply saved, suggested, or forgot: a compact receipt row, or a bordered
 * decision card for a suggestion. The reply's snapshot stands in until the memory list settles.
 * @registry Scout memory receipt row (Undo/Manage) or suggestion card (Save/No thanks).
 */
export const AssistantMemoryCard: FC<AssistantMemoryCardProps> = ({
  note,
  action = 'saved',
  onManage,
}) => {
  const run = useMemoryRequest();
  const forgotten = action === 'forgotten';
  const {data: memory, isFetching} = useGetAssistantMemoryQuery(undefined, {
    skip: forgotten,
  });
  const [acceptMemory, {isLoading: isAccepting}] =
    useAcceptAssistantMemoryMutation();
  const [deleteMemory, {isLoading: isDeleting}] =
    useDeleteAssistantMemoryMutation();

  const live = memory?.notes.find(n => n.id === note.id);
  const settled = memory !== undefined && !isFetching;
  const current = forgotten ? note : (live ?? (settled ? null : note));
  if (!current) return null;

  if (!forgotten && isProposedMemory(current)) {
    return (
      <Paper withBorder p="xs">
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Box c="dimmed" style={{display: 'flex', flexShrink: 0, marginTop: 2}}>
            <IconBrain size={14} aria-hidden />
          </Box>
          <Stack gap={4} style={{minWidth: 0, flex: 1}}>
            <Text size="xs" fw={600}>
              {`Scout suggests remembering this · ${memoryKindLabel(current.kind)}`}
            </Text>
            <Text size="sm" style={{overflowWrap: 'anywhere'}}>
              {current.text}
            </Text>
            <Group gap="xs">
              <Button
                size="compact-xs"
                loading={isAccepting}
                disabled={isDeleting}
                onClick={() => run(() => acceptMemory(current.id).unwrap())}>
                Save
              </Button>
              <Button
                variant="subtle"
                size="compact-xs"
                loading={isDeleting}
                disabled={isAccepting}
                onClick={() => run(() => deleteMemory(current.id).unwrap())}>
                No thanks
              </Button>
            </Group>
          </Stack>
        </Group>
      </Paper>
    );
  }

  const canUndo =
    !forgotten && current.source === AssistantMemorySourceEnum.UserRequest;
  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <Box c="dimmed" style={{display: 'flex', flexShrink: 0}}>
        <IconBrain size={14} aria-hidden />
      </Box>
      <Text size="sm" c="dimmed" style={{minWidth: 0, overflowWrap: 'anywhere'}}>
        {`${forgotten ? 'Removed from memory' : 'Saved to memory'}: ${current.text}`}
      </Text>
      {canUndo && (
        <Button
          variant="subtle"
          size="compact-xs"
          loading={isDeleting}
          onClick={() => run(() => deleteMemory(current.id).unwrap())}>
          Undo
        </Button>
      )}
      <Button variant="subtle" size="compact-xs" onClick={onManage}>
        Manage
      </Button>
    </Group>
  );
};
